<template>
    <div>
    <v-data-table
        ref="dataTable"
        :headers="visibleHeaders"
        :items="tableItems"
        :loading="loading || tabelaFrontHeadersLoading"
        :loading-text="loadingText"
        :items-per-page="itemsPerPage"
        :server-items-length="serverItemsLength != null ? serverItemsLength : undefined"
        :item-class="resolveItemClass"
        :show-select="showSelect"
        :single-select="singleSelect"
        :last-item="itemsPerPageLastOption"
        :item-key="itemKey"
        :footer-props="footerProps"
        :search="search"
        :height="effectiveTableHeight"
        :fixed-header="effectiveFixedHeader"
        :mobile-breakpoint="useVirtualBody ? 0 : undefined"
        v-model="selectedItems"
        :value="effectiveSelection"
        @input="onSelectionInput"
        dense
        class="elevation-7"
        :class="{ 'elevation-7': !flat, 'merus-dt-virtualized': useVirtualBody }"
        locale="pt"
        :disable-sort="true"
        :sort-by="serverItemsLength != null ? serverSortBy : clientSortBy"
        :sort-desc="serverItemsLength != null ? serverSortDesc : clientSortDesc"
        :style="flat ? null : 'border-radius: 10px; margin-bottom: 10px; margin-top: 10px'"
        :show-expand="showExpand"
        :single-expand="singleExpand"
        :expanded.sync="expandedItems"
        @update:options="onUpdateOptions"
        @item-selected="onItemSelected"
        @toggle-select-all="onToggleSelectAll"
        @click:row="onClickRow"
    >
        <template v-if="hideSelectAll" v-slot:header.data-table-select />
        <template v-slot:top>
            <slot name="toolbar"></slot>

            <!-- Botão Mostrar Colunas -->
            <v-menu
                v-if="effectiveShowColumnsButton"
                min-width="220px"
                max-width="320px"
                :close-on-content-click="false">
                <template v-slot:activator="{ on: onMenu }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on: onTooltip }">
                            <v-btn icon small color="primary" class="ml-3" v-on="{ ...onMenu, ...onTooltip }">
                                <v-icon>mdi-format-columns</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('system.labels.showColumns') }}</span>
                    </v-tooltip>
                </template>
                <v-card>
                    <v-card-title class="subtitle-1 pb-1">
                        <strong>{{ $t('system.labels.showColumns') }}</strong>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                        <v-list dense style="max-height: 300px; overflow-y: auto;">
                            <v-list-item
                                v-for="(header, i) in toggleableHeaders"
                                :key="`col-toggle-${i}`"
                                dense>
                                <v-list-item-action class="my-0 mr-3">
                                    <v-checkbox
                                        v-model="header.show"
                                        color="primary"
                                        hide-details
                                        dense
                                        @change="onColumnVisibilityChange">
                                    </v-checkbox>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>{{ header.text }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-menu>

            <!-- Botão Configurar Colunas -->
            <v-tooltip v-if="effectiveShowConfigureColumnsButton" top>
                <template v-slot:activator="{ on: onTooltip }">
                    <v-btn icon small color="primary" class="ml-3" v-on="onTooltip" @click="openAddColumnDialog">
                        <v-icon>mdi-cog</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('system.labels.configureColumns') }}</span>
            </v-tooltip>

            <!-- Botão Ordenar -->
            <v-menu 
                v-if="!hideSortButton"
                min-width="30%"
                max-width="40%"
                :close-on-content-click="false">
                <template v-slot:activator="{ on }">
                <v-btn icon small color="primary" @click="sortDialog = true" v-on="on" class="ml-3">
                    <v-icon>mdi-sort</v-icon>
                </v-btn>
                </template>
                <v-card>
                <v-card-title>
                    <strong>{{ $t('system.labels.orderBy') }}</strong>
                </v-card-title>
                <v-card-text>
                    <v-row v-for="(sort, k) in multiSort" :key="k">
                    <v-col cols="12" sm="5">
                        <v-autocomplete
                        :items="sortableHeaders"
                        item-value="value"
                        item-text="text"
                        :key="k"
                        v-model="sort.value"
                        dense
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="5">
                        <v-autocomplete
                        :items="sortOptions"
                        return-object
                        item-text="text"
                        :key="k"
                        v-model="sort.options"
                        dense
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="2" align-self="center">
                        <v-icon @click="eraseSort(k)">mdi-close</v-icon>
                    </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn fab dark color="primary" x-small @click="addSort" :disabled="sortableHeaders.length < 1"><v-icon>mdi-plus</v-icon></v-btn>
                </v-card-actions>
                </v-card>
            </v-menu>
        </template>

        <template
              v-for="(header, i) in filtrableHeaders"
              v-slot:[`header.${header.value}`]="{ }"
            >
            <v-edit-dialog @click.stop :key="i">
              {{ headerFilterLabel(header) }}
                <template v-slot:input>
                  <div
                    v-if="isDateRangeHeader(header)"
                    :key="`range-${i}`"
                    class="date-range-filter pa-2"
                    style="min-width: 220px;"
                    @click.stop
                  >
                    <v-menu
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                      :value="isDateMenuOpen(header.value, 'from')"
                      @input="setDateMenuOpen(header.value, 'from', $event)"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDateDisplay(getDateRangeBound(header.value, 'from'))"
                          :label="$t('datetime.from')"
                          readonly
                          dense
                          clearable
                          hide-details
                          class="mb-2 input__filter"
                          prepend-inner-icon="mdi-calendar"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear.stop="setDateRangeBound(header.value, 'from', null)"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        :value="getDateRangeBound(header.value, 'from')"
                        no-title
                        scrollable
                        locale="pt-BR"
                        @input="onDateRangePick(header.value, 'from', $event)"
                      ></v-date-picker>
                    </v-menu>
                    <v-menu
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                      :value="isDateMenuOpen(header.value, 'to')"
                      @input="setDateMenuOpen(header.value, 'to', $event)"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDateDisplay(getDateRangeBound(header.value, 'to'))"
                          :label="$t('datetime.until')"
                          readonly
                          dense
                          clearable
                          hide-details
                          class="mb-2 input__filter"
                          prepend-inner-icon="mdi-calendar"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear.stop="setDateRangeBound(header.value, 'to', null)"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        :value="getDateRangeBound(header.value, 'to')"
                        no-title
                        scrollable
                        locale="pt-BR"
                        @input="onDateRangePick(header.value, 'to', $event)"
                      ></v-date-picker>
                    </v-menu>
                    <v-btn text x-small color="primary" class="px-0" @click="clearDateRange(header.value)">
                      {{ $t('button.clear') }}
                    </v-btn>
                  </div>
                  <v-text-field
                    v-else
                    :key="i"
                    :value="multiSearch[header.value]"
                    type="text"
                    single-line
                    class="input__filter"
                    @input="onFilterTextInput(header.value, $event)"
                  ></v-text-field>
                </template>
              </v-edit-dialog>
        </template>

        <!--
          Virtualização: com muitas linhas na página atual, só monta as linhas visíveis no DOM.
          Desligada com showExpand (altura variável) ou abaixo do limiar (usa body/slots padrão).
        -->
        <template v-if="useVirtualBody" v-slot:body="bodySlot">
            <tbody>
                <tr
                    v-if="virtualTopSpacerPx(bodySlot.items) > 0"
                    class="merus-dt-virtual-spacer"
                    aria-hidden="true"
                >
                    <td :colspan="bodySlot.headers.length">
                        <div
                            class="merus-dt-virtual-spacer__fill"
                            :style="{ height: `${virtualTopSpacerPx(bodySlot.items)}px` }"
                        ></div>
                    </td>
                </tr>
                <tr
                    v-for="(item, rowIndex) in virtualVisibleItems(bodySlot.items)"
                    :key="`vr-${getItemKey(item)}-${virtualRangeForItems(bodySlot.items).start + rowIndex}`"
                    :class="resolveItemClass(item)"
                    @click="onClickRow(item, { item })"
                >
                        <td
                            v-for="header in bodySlot.headers"
                            :key="`vc-${header.value}`"
                            :class="header.align ? `text-${header.align}` : undefined"
                        >
                            <template v-if="header.value === 'data-table-select'">
                                <v-simple-checkbox
                                    :value="bodySlot.isSelected(item)"
                                    :ripple="false"
                                    @input="bodySlot.select(item, $event)"
                                    @click.stop
                                />
                            </template>
                            <template v-else-if="header.value === 'data-table-expand'">
                                <v-icon
                                    small
                                    @click.stop="bodySlot.expand(item, !bodySlot.isExpanded(item))"
                                >
                                    {{ bodySlot.isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                </v-icon>
                            </template>
                            <template v-else-if="header.value === itemSelectColumnValue">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            x-small
                                            v-bind="attrs"
                                            v-on="on"
                                            :color="isItemSelectActive(item) ? 'primary' : undefined"
                                            @click.stop="onItemSelectClick(item)"
                                        >
                                            <v-icon small>
                                                {{ isItemSelectActive(item) ? itemSelectIconActive : itemSelectIcon }}
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ itemSelectTooltip || $t('system.labels.selectItem') }}</span>
                                </v-tooltip>
                            </template>
                            <slot
                                v-else
                                :name="`item.${header.value}`"
                                v-bind="virtualRowSlotProps(item, header, bodySlot, virtualRangeForItems(bodySlot.items).start + rowIndex)"
                            >
                                {{ item[header.value] }}
                            </slot>
                        </td>
                </tr>
                <tr
                    v-if="virtualBottomSpacerPx(bodySlot.items) > 0"
                    class="merus-dt-virtual-spacer"
                    aria-hidden="true"
                >
                    <td :colspan="bodySlot.headers.length">
                        <div
                            class="merus-dt-virtual-spacer__fill"
                            :style="{ height: `${virtualBottomSpacerPx(bodySlot.items)}px` }"
                        ></div>
                    </td>
                </tr>
            </tbody>
        </template>

        <!-- Select exclusivo por linha (body padrão do Vuetify; ignorado quando useVirtualBody) -->
        <template v-if="showItemSelect" v-slot:[`item.${itemSelectColumnValue}`]="slotProps">
            <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        x-small
                        v-bind="attrs"
                        v-on="on"
                        :color="isItemSelectActive(slotProps.item) ? 'primary' : undefined"
                        @click.stop="onItemSelectClick(slotProps.item)"
                    >
                        <v-icon small>
                            {{ isItemSelectActive(slotProps.item) ? itemSelectIconActive : itemSelectIcon }}
                        </v-icon>
                    </v-btn>
                </template>
                <span>{{ itemSelectTooltip || $t('system.labels.selectItem') }}</span>
            </v-tooltip>
        </template>

        <!-- Slots de célula: repassamos todas as props do v-data-table (item, isExpanded, expand, etc.) -->
        <template v-for="(header) in dataCellHeaders" v-slot:[`item.${header.value}`]="slotProps">
            <slot :name="`item.${header.value}`" v-bind="slotProps">
                {{ slotProps.item[header.value] }}
            </slot>
        </template>
       <template v-if="showExpand" v-slot:expanded-item="{ headers, item }">
            <td :colspan="totalVisibleColumns" style="margin: 0; padding: 5px; width: 100%;">
                <slot name="expanded-item" v-bind:headers="headers" v-bind:item="item">
                    <div style="padding: 10px;">
                        <p>Conteúdo expandido não definido. Use o slot 'expanded-item' para personalizar.</p>
                    </div>
                </slot>
            </td>
        </template>
        <template v-slot:no-data>
            <slot name="no-data"></slot>
        </template>
    </v-data-table>

    <v-dialog v-model="addColumnDialog" max-width="560" persistent>
        <v-card>
            <v-card-title class="subtitle-1 pb-1">
                <strong>{{ $t('system.labels.addColumn') }}</strong>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-4">
                <v-alert v-if="availableItemFields.length === 0" type="info" dense text>
                    {{ $t('system.labels.noAvailableFields') }}
                </v-alert>
                <v-form v-else ref="addColumnForm" v-model="addColumnFormValid">
                    <v-autocomplete
                        v-model="newColumn.value"
                        :items="availableItemFields"
                        :label="$t('system.labels.selectField')"
                        :rules="[(v) => !!v || $t('system.labels.selectField')]"
                        dense
                        outlined
                        hide-details="auto"
                        class="mb-4"
                        @change="onNewColumnFieldSelected"
                    ></v-autocomplete>
                    <v-text-field
                        v-model="newColumn.text"
                        :label="$t('system.labels.columnName')"
                        :rules="[(v) => !!v || $t('system.labels.columnName')]"
                        dense
                        outlined
                        hide-details="auto"
                        class="mb-4"
                        @input="onNewColumnTextInput"
                    ></v-text-field>
                    <v-text-field
                        v-model="newColumn.label"
                        :label="$t('system.labels.columnLabel')"
                        dense
                        outlined
                        hide-details="auto"
                        class="mb-2"
                        @input="newColumnLabelTouched = true"
                    ></v-text-field>
                    <v-checkbox
                        v-model="newColumn.to_show"
                        :label="$t('system.labels.showOnScreen')"
                        color="primary"
                        hide-details
                        class="mt-0"
                    ></v-checkbox>
                    <v-checkbox
                        v-model="newColumn.to_export"
                        :label="$t('system.labels.exportCsv')"
                        color="primary"
                        hide-details
                        class="mt-0"
                    ></v-checkbox>
                </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="primary" :disabled="addColumnSaving" @click="closeAddColumnDialog">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn color="primary" :loading="addColumnSaving" :disabled="availableItemFields.length === 0"
                    @click="saveNewColumn">
                    {{ $t('button.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    </div>
</template>
<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";
import {
    appendActionsColumn,
    buildTabelaFrontHeaders,
    mergeTabelaFrontHeadersSession,
    persistTabelaFrontHeadersSession,
} from "@/utils/tabelasFrontHeaders";
export default {
    name: "MerusDataTable",
    props: {
        headers: {
            type: Array,
            default() {
                return [];
            }
        },
        items: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean, 
            default() {
                return false;
            }
        },
        loadingText: {
            type: String,
            default() {
                return this.$t('system.messages.loading');
            }
        },
        itemsPerPage: {
            type: Number,
            default() {
                return 10;
            }
        },
        itemClass: {
            type: [Function, String, null],
            default() {
                return null;
            }
        },
        defaultMultiSort: {
            type: [Array, null],
            default() {
                return null
            }
        },
        hideSortButton: {
            type: Boolean,
            default() {
                return false
            }
        },
        flat: {
            type: Boolean,
            default() {
                return false;
            }
        },
        showSelect: {
            type: Boolean,
            default() {
                return false
            }
        },
        singleSelect: {
            type: Boolean,
            default() {
                return false
            }
        },
        /**
         * Exibe um botão de select por linha (seleção exclusiva).
         * Ao clicar, desmarca o item anterior e marca o novo; clique no ativo desmarca.
         * Ação do usuário: prop itemSelectAction e/ou evento @item-select.
         */
        showItemSelect: {
            type: Boolean,
            default: false
        },
        /** Item atualmente ativo do select por linha (suporta .sync). */
        selectedItem: {
            type: Object,
            default: null
        },
        /** Função chamada ao (de)selecionar: (item|null) => void */
        itemSelectAction: {
            type: Function,
            default: null
        },
        itemSelectIcon: {
            type: String,
            default: 'mdi-checkbox-blank-outline'
        },
        itemSelectIconActive: {
            type: String,
            default: 'mdi-checkbox-marked'
        },
        itemSelectTooltip: {
            type: String,
            default: null
        },
        hideSelectAll: {
            type: Boolean,
            default() {
                return false
            }
        },
        showExpand: {
            type: Boolean,
            default() {
                return false;
            }
        },
        singleExpand: {
            type: Boolean,
            default() {
                return false;
            }
        },
        itemKey: {
            type: String,
            default() {
                return 'id';
            }
        },
        mostrarInativos: {
            type: Boolean,
            default() {
                return true;
            }
        },
        serverItemsLength: {
            type: Number,
            default: null
        },
        serverSortBy: {
            type: [String, Array],
            default: null
        },
        serverSortDesc: {
            type: [Boolean, Array],
            default: false
        },
        value: {
            type: Array,
            default: () => []
        },
        showColumnsButton: {
            type: Boolean,
            default: false
        },
        tableHeight: {
            type: [String, Number],
            default: null
        },
        fixedHeader: {
            type: Boolean,
            default: false
        },
        search: {
            type: String,
            default: null
        },
        /**
         * Valor da última opção em "Itens por página" (ex.: 1000). Se total > limite, vira -1 (Todos no Vuetify).
         * Cada tela pode definir o seu.
         */
        itemsPerPageLastOption: {
            type: Number,
            default() {
                return 1000;
            }
        },
        /**
         * Quando true, páginas com muitas linhas usam virtualização (só DOM das linhas visíveis).
         * Desligada automaticamente se showExpand (altura de linha variável).
         */
        virtualizeRows: {
            type: Boolean,
            default: true
        },
        /** Quantidade de linhas na página atual a partir da qual a virtualização liga. */
        virtualizeThreshold: {
            type: Number,
            default: 50
        },
        /** Altura estimada de cada linha dense (px) para o cálculo da janela virtual. */
        virtualRowHeight: {
            type: Number,
            default: 32
        },
        /**
         * Altura do viewport da tabela quando virtualiza e tableHeight não foi informado.
         * Necessária para o scroll interno do fixed-header.
         */
        virtualViewportHeight: {
            type: [String, Number],
            default: 480
        },
        /**
         * Código em tabelas_front (ex.: PASSAGEIROS). Com isso, colunas configuráveis
         * (mostrar/ocultar, engrenagem, persistência) ficam ativas por padrão.
         * Use disable-tabela-front-config para desativar.
         */
        codTabela: {
            type: String,
            default: null
        },
        /** Opt-out: desativa carregamento/persistência via headers_tabelas_front mesmo com cod-tabela. */
        disableTabelaFrontConfig: {
            type: Boolean,
            default: false
        },
        /** Opt-out: oculta o botão de mostrar/ocultar colunas (somente com cod-tabela). */
        hideColumnsButton: {
            type: Boolean,
            default: false
        },
        /** Opt-out: oculta o botão de engrenagem para adicionar colunas (somente com cod-tabela). */
        hideConfigureColumnsButton: {
            type: Boolean,
            default: false
        },
        /** null = detecta slot item.actions; true/false força inclusão ou exclusão da coluna. */
        appendActions: {
            type: Boolean,
            default: null
        },
        actionsI18nKey: {
            type: String,
            default: "system.labels.actions"
        },
        actionsText: {
            type: String,
            default: null
        },
        actionsShow: {
            type: Boolean,
            default: true
        },
        sessionKey: {
            type: String,
            default: null
        },
        token: {
            type: String,
            default: null
        },
        /** Colunas dinâmicas inseridas antes de "actions" (ex.: turnos em candidatos). */
        extraHeaders: {
            type: Array,
            default() {
                return [];
            }
        },
        /** Valores de colunas ocultadas da configuração base (ex.: turnos gerenciados via extraHeaders). */
        suppressHeaderValues: {
            type: Array,
            default() {
                return [];
            }
        }
    },
    data: () => ({
        multiSearch: {},
        /** Cópia debounced de multiSearch usada em filteredItems (evita filtrar a cada tecla). */
        appliedMultiSearch: {},
        multiSort: [],
        selectedItems: [],
        expandedItems: [],
        localHeaders: [],
        dateRangeMenus: {},
        _filterEmitTimeout: null,
        _sortEmitTimeout: null,
        _syncingSortFromServer: false,
        _syncingFromValue: false,
        addColumnDialog: false,
        addColumnFormValid: false,
        addColumnSaving: false,
        newColumnLabelTouched: false,
        newColumn: {
            value: null,
            text: '',
            label: '',
            to_show: true,
            to_export: true,
        },
        tabelaFrontHeaders: [],
        tabelaFrontHeadersLoading: false,
        internalSelectedItem: null,
        itemSelectColumnValue: 'data-table-item-select',
        tableOptions: {
            page: 1,
            itemsPerPage: 10,
        },
        virtualScrollTop: 0,
        virtualViewportHeightPx: 0,
        virtualHeaderHeightPx: 0,
    }),
    created() {
        // Headers locais primeiro (evita grade sem colunas enquanto a API de tabelas_front responde).
        this.initLocalHeaders();
        if (this.tabelaFrontEnabled) {
            this.loadTabelaFrontHeaders();
        }
        if (this.defaultMultiSort) {
            this.multiSort = this.defaultMultiSort;
        }
        if (this.value && this.value.length > 0) {
            this.selectedItems = [...this.value];
        }
        this.internalSelectedItem = this.selectedItem;
        this.tableOptions = {
            page: 1,
            itemsPerPage: this.itemsPerPage,
        };
    },
    mounted() {
        this.ensureMultiSearchKeys();
        // Garante que filteredItems usa o mesmo mapa inicial (mesmo se ensure não criou chaves novas).
        if (!Object.keys(this.appliedMultiSearch).length) {
            this.scheduleApplyMultiSearch(true);
        }
        if (this.value && this.value.length > 0 && this.selectedItems.length === 0) {
            this.selectedItems = [...this.value];
        }
        if (this.useVirtualBody) {
            this.$nextTick(() => this.attachVirtualScroll());
        }
    },
    beforeDestroy() {
        this.detachVirtualScroll();
        if (this._filterEmitTimeout) clearTimeout(this._filterEmitTimeout);
        if (this._sortEmitTimeout) clearTimeout(this._sortEmitTimeout);
    },
    watch: {
        useVirtualBody(enabled) {
            this.$nextTick(() => {
                if (enabled) {
                    this.attachVirtualScroll();
                } else {
                    this.detachVirtualScroll();
                    this.virtualScrollTop = 0;
                }
            });
        },
        tableItems() {
            if (this.useVirtualBody) {
                this.resetVirtualScrollPosition();
            }
        },
        'tableOptions.page'() {
            if (this.useVirtualBody) {
                this.resetVirtualScrollPosition();
            }
        },
        'tableOptions.itemsPerPage'() {
            if (this.useVirtualBody) {
                this.resetVirtualScrollPosition();
            }
        },
        selectedItem: {
            handler(val) {
                this.internalSelectedItem = val;
            },
            deep: true
        },
        value: {
            handler(val) {
                if (!val || !Array.isArray(val)) return;
                this._syncingFromValue = true;
                this.selectedItems = val.length > 0 ? [...val] : [];
                this.$nextTick(() => { this._syncingFromValue = false; });
            },
            deep: true,
            immediate: false
        },
        selectedItems: {
            handler(val) {
                if (!this._syncingFromValue) {
                    this.$emit('input', val || []);
                }
            },
            deep: true
        },
        filtrableHeaders: {
            handler() {
                this.ensureMultiSearchKeys();
            },
            immediate: false
        },
        multiSearch: {
            // Backup: mudanças feitas fora dos handlers (ex.: limpeza programática).
            // Digitação e date-range usam scheduleApplyMultiSearch diretamente.
            handler() {
                this.scheduleApplyMultiSearch(false);
            },
            deep: true
        },
        multiSort: {
            handler(val) {
                if (this._syncingSortFromServer) return;
                if (this.serverItemsLength != null && val && val.length > 0) {
                    if (this._sortEmitTimeout) clearTimeout(this._sortEmitTimeout);
                    this._sortEmitTimeout = setTimeout(() => {
                        if (this._syncingSortFromServer) return;
                        const sortBy = this.multiSort.map((s) => s.value);
                        const sortDesc = this.multiSort.map((s) => s.options && s.options.value === -1);
                        const currentBy = Array.isArray(this.serverSortBy) ? this.serverSortBy : (this.serverSortBy != null ? [this.serverSortBy] : []);
                        const currentDesc = Array.isArray(this.serverSortDesc) ? this.serverSortDesc : (this.serverSortDesc !== false && this.serverSortDesc !== undefined ? [true] : [false]);
                        const same = sortBy.length === currentBy.length && sortBy.every((c, i) => c === currentBy[i]) && sortDesc.length === currentDesc.length && sortDesc.every((d, i) => !!d === !!currentDesc[i]);
                        if (!same && sortBy.every(Boolean)) {
                            this.$emit('update:options', { sortBy, sortDesc, page: 1 });
                        }
                        this._sortEmitTimeout = null;
                    }, 300);
                }
            },
            deep: true
        },
        serverSortBy: {
            handler(val) {
                if (this.serverItemsLength != null && this.sortOptions.length >= 2) {
                    const byArr = Array.isArray(val) ? val : (val != null ? [val] : []);
                    const descArr = Array.isArray(this.serverSortDesc) ? this.serverSortDesc : (this.serverSortDesc ? [true] : [false]);
                    if (byArr.length === 0) return;
                    const next = byArr.map((col, i) => ({
                        value: col,
                        options: descArr[i] ? this.sortOptions[1] : this.sortOptions[0]
                    }));
                    const same = this.multiSort.length === next.length && this.multiSort.every((s, i) => s.value === next[i].value && s.options && s.options.value === next[i].options.value);
                    if (!same) {
                        this._syncingSortFromServer = true;
                        this.multiSort = next;
                        this.$nextTick(() => { this._syncingSortFromServer = false; });
                    }
                }
            }
        },
        serverSortDesc: {
            handler(val) {
                if (this.serverItemsLength != null && this.serverSortBy != null && this.sortOptions.length >= 2) {
                    const byArr = Array.isArray(this.serverSortBy) ? this.serverSortBy : [this.serverSortBy];
                    const descArr = Array.isArray(val) ? val : [!!val];
                    const next = byArr.map((col, i) => ({
                        value: col,
                        options: descArr[i] ? this.sortOptions[1] : this.sortOptions[0]
                    }));
                    const same = this.multiSort.length === next.length && this.multiSort.every((s, i) => s.value === next[i].value && s.options && s.options.value === next[i].options.value);
                    if (!same) {
                        this._syncingSortFromServer = true;
                        this.multiSort = next;
                        this.$nextTick(() => { this._syncingSortFromServer = false; });
                    }
                }
            }
        },
        headers: {
            handler() {
                if (!this.tabelaFrontEnabled) {
                    this.initLocalHeaders();
                }
            },
            deep: true
        },
        tabelaFrontHeaders: {
            handler() {
                if (this.tabelaFrontEnabled) {
                    this.initLocalHeaders();
                }
            },
            deep: true
        },
        codTabela: {
            handler(val, oldVal) {
                if (val !== oldVal) {
                    if (this.tabelaFrontEnabled) {
                        this.loadTabelaFrontHeaders();
                    } else {
                        this.tabelaFrontHeaders = [];
                        this.initLocalHeaders();
                    }
                }
            }
        },
        disableTabelaFrontConfig(val) {
            if (val) {
                this.tabelaFrontHeaders = [];
                this.initLocalHeaders();
            } else if (this.codTabela) {
                this.loadTabelaFrontHeaders();
            }
        },
        extraHeaders: {
            handler() {
                this.initLocalHeaders();
                this.$emit('update:headers', this.localHeaders.map((h) => ({ ...h })));
            },
            deep: true
        },
        suppressHeaderValues: {
            handler() {
                this.initLocalHeaders();
                this.$emit('update:headers', this.localHeaders.map((h) => ({ ...h })));
            },
            deep: true
        },
        '$i18n.locale'() {
            if (this.tabelaFrontEnabled) {
                this.loadTabelaFrontHeaders();
            }
        },
        filteredItems: {
            handler(val) {
                // Client-side: filteredItems is the full filtered set (not just the current page).
                // Server-side tables must not emit here — `items` is only the current page
                // (capped by items-per-page). Export should use loadItemsForExport instead.
                if (this.serverItemsLength == null && val) {
                    this.$emit('update:displayedItems', val);
                }
            },
            immediate: true
        }
    },
    computed: {
        effectiveSelection() {
            return this.value !== undefined ? this.value : this.selectedItems;
        },
        clientSortBy() {
            if (!this.multiSort || this.multiSort.length === 0) return undefined;
            if (this.multiSort.length === 1) return this.multiSort[0].value;
            return this.multiSort.map((s) => s.value);
        },
        clientSortDesc() {
            if (!this.multiSort || this.multiSort.length === 0) return false;
            const desc = this.multiSort.map((s) => s.options && s.options.value === -1);
            if (desc.length === 1) return desc[0];
            return desc;
        },
        toggleableHeaders() {
            return this.localHeaders.filter((h) => !h.align && h.value !== 'actions');
        },
        visibleHeaders() {
            const headers = this.localHeaders.filter((h) => h.show !== false);
            if (!this.showItemSelect) {
                return headers;
            }
            return [
                {
                    text: '',
                    value: this.itemSelectColumnValue,
                    sortable: false,
                    filtrable: false,
                    width: '48px',
                    align: 'center',
                    show: true,
                },
                ...headers,
            ];
        },
        dataCellHeaders() {
            return this.visibleHeaders.filter((h) => h.value !== this.itemSelectColumnValue);
        },
        filtrableHeaders() {
            return this.dataCellHeaders.filter((h) => h.filtrable);
        },
        sortableHeaders() {
            return this.visibleHeaders.filter((h) => h.sortable);
        },
        
        totalVisibleColumns() {
            return this.visibleHeaders.length + (this.showExpand ? 1 : 0);
        },
        filteredItems() {
            let itemsFiltrados = this.items;
            if (!this.mostrarInativos) {
                itemsFiltrados = this.items.filter((item) => this.isItemAtivo(item));
            }
            const hasActiveSort = this.multiSort && this.multiSort.length > 0;
            const applyMultiSort = (list) => {
                if (!hasActiveSort) {
                    return list;
                }
                return [...list].sort((a, b) => {
                    let result = 0;
                    let weight = this.multiSort.length;
                    for (let sort of this.multiSort) {
                        if (a[sort.value] > b[sort.value]) {
                            result += 1 * sort.options.value * weight;
                        } else if (a[sort.value] == b[sort.value]) {
                            result += 0;
                        } else {
                            result += -1 * sort.options.value * weight;
                        }
                        weight--;
                    }
                    return result;
                });
            };
            if (Object.keys(this.appliedMultiSearch).length > 0) {
                const filtered = itemsFiltrados.filter((item) => {
                    return Object.entries(this.appliedMultiSearch).every(([key, value]) => {
                        if (value != null && typeof value === 'object' && !Array.isArray(value)) {
                            const from = value.from != null && String(value.from).trim() !== '' ? String(value.from).trim() : null;
                            const to = value.to != null && String(value.to).trim() !== '' ? String(value.to).trim() : null;
                            if (!from && !to) return true;
                            return this.matchesDateRange(item[key], { from, to });
                        }
                        if (value == null || String(value).trim() === '') return true;
                        return (item[key] ?? "").toString().toUpperCase().includes(value.toString().toUpperCase());
                    });
                });
                return applyMultiSort(filtered);
            }
            return applyMultiSort(itemsFiltrados);
       },
        tableItems() {
            if (this.serverItemsLength != null) {
                if (!this.mostrarInativos) {
                    return this.items.filter((item) => this.isItemAtivo(item));
                }
                return this.items;
            }
            return this.filteredItems;
        },
        /** Linhas esperadas na página atual (após paginação do v-data-table). */
        currentPageItemCount() {
            const itemsLen = (this.tableItems || []).length;
            // Server-side: `items` já é só a página atual.
            if (this.serverItemsLength != null) {
                return itemsLen;
            }
            const per = this.tableOptions.itemsPerPage != null
                ? this.tableOptions.itemsPerPage
                : this.itemsPerPage;
            if (per === -1 || per == null) {
                return itemsLen;
            }
            const page = this.tableOptions.page || 1;
            const start = (page - 1) * per;
            return Math.min(per, Math.max(0, itemsLen - start));
        },
        useVirtualBody() {
            if (!this.virtualizeRows || this.showExpand) {
                return false;
            }
            return this.currentPageItemCount > this.virtualizeThreshold;
        },
        effectiveTableHeight() {
            if (this.tableHeight != null && this.tableHeight !== '') {
                return this.tableHeight;
            }
            if (this.useVirtualBody) {
                return this.virtualViewportHeight;
            }
            return null;
        },
        effectiveFixedHeader() {
            return this.fixedHeader || this.useVirtualBody;
        },
        sortOptions() {
            return [
                { text: this.$t('system.labels.increasing'), value: 1},
                { text: this.$t('system.labels.decreasing'), value: -1}]
        },
        footerProps() {
            const total = this.serverItemsLength != null ? this.serverItemsLength : this.items.length;
            const lastOption = total > this.itemsPerPageLastOption ? this.itemsPerPageLastOption : -1;
            return {
                'items-per-page-options': [5, 10, 15, lastOption]
            };
        },
        tabelaFrontEnabled() {
            return !!this.codTabela && !this.disableTabelaFrontConfig;
        },
        effectiveSessionKey() {
            return this.sessionKey || (this.codTabela ? `headers_${this.codTabela}` : null);
        },
        effectiveShowColumnsButton() {
            if (this.tabelaFrontEnabled) {
                return !this.hideColumnsButton;
            }
            return this.showColumnsButton;
        },
        effectiveShowConfigureColumnsButton() {
            return this.tabelaFrontEnabled && !this.hideConfigureColumnsButton;
        },
        hasActionsSlot() {
            return !!(this.$scopedSlots["item.actions"] || this.$slots["item.actions"]);
        },
        effectiveAppendActions() {
            if (this.appendActions === false) return false;
            if (this.appendActions === true) return true;
            return this.hasActionsSlot;
        },
        actionsColumnOptions() {
            return {
                appendActions: this.effectiveAppendActions,
                actionsText: this.actionsText,
                actionsI18nKey: this.actionsI18nKey,
                actionsShow: this.actionsShow,
                translateFn: (key) => this.$t(key),
            };
        },
        sourceHeadersForInit() {
            let headers;
            if (this.tabelaFrontEnabled && this.tabelaFrontHeaders.length > 0) {
                headers = this.tabelaFrontHeaders;
            } else {
                headers = this.headers || [];
            }
            const suppressed = new Set(this.suppressHeaderValues || []);
            if (suppressed.size) {
                headers = headers.filter((h) => !suppressed.has(h.value));
            }
            const extra = (this.extraHeaders || []).filter((h) => h && h.value);
            if (extra.length) {
                const withoutActions = headers.filter((h) => h.value !== 'actions');
                const actions = headers.find((h) => h.value === 'actions');
                headers = actions ? [...withoutActions, ...extra, actions] : [...withoutActions, ...extra];
            }
            return appendActionsColumn(headers, this.actionsColumnOptions);
        },
        existingHeaderValues() {
            return new Set((this.localHeaders || []).map((h) => h.value));
        },
        availableItemFields() {
            const existing = this.existingHeaderValues;
            const keys = new Set();
            const sample = (this.items || []).slice(0, 100);
            sample.forEach((item) => {
                if (!item || typeof item !== 'object') return;
                Object.keys(item).forEach((key) => {
                    if (existing.has(key)) return;
                    if (key === 'actions' || key === 'data-table-expand') return;
                    if (key.startsWith('_')) return;
                    keys.add(key);
                });
            });
            return Array.from(keys).sort().map((value) => ({
                text: value,
                value,
            }));
        },
    },
    methods: {
        isItemAtivo(item) {
            if (item.id_situacao_ativa !== undefined && item.id_situacao_ativa !== null) {
                return item.id_situacao_ativa !== -1;
            }
            return item.ativo === true || item.ativo === 1 ||
                item.ativa === true || item.ativa === 1;
        },
        onClickRow(item, data, event) {
            // Vuetify 2: emit('click:row', item, data, event)
            // Corpo virtual: onClickRow(item, { item })
            const row = (data && data.item != null) ? data.item : item;
            if (!row || typeof row !== 'object') return;
            this.$emit('click:row', row, data && typeof data === 'object' ? data : { item: row }, event);
        },
        initLocalHeaders() {
            const prev = this.localHeaders.reduce((acc, h) => {
                acc[h.value] = h.show;
                return acc;
            }, {});
            let source = this.sourceHeadersForInit;
            if (this.effectiveSessionKey && !this.tabelaFrontEnabled) {
                source = mergeTabelaFrontHeadersSession(source, this.effectiveSessionKey);
            }
            this.localHeaders = source.map((h) => ({
                ...h,
                show: Object.prototype.hasOwnProperty.call(prev, h.value)
                    ? prev[h.value]
                    : (h.show !== false && h.show !== 0),
            }));
        },
        loadTabelaFrontHeaders(clearSession = false) {
            if (!this.tabelaFrontEnabled) {
                return Promise.resolve(this.headers || []);
            }
            if (clearSession && this.effectiveSessionKey) {
                sessionStorage.removeItem(this.effectiveSessionKey);
            }
            this.tabelaFrontHeadersLoading = true;
            return buildTabelaFrontHeaders(this.codTabela, this.token, {
                sessionKey: this.effectiveSessionKey,
                appendActions: this.effectiveAppendActions,
                actionsText: this.actionsText,
                actionsI18nKey: this.actionsI18nKey,
                actionsShow: this.actionsShow,
                translateFn: (key) => this.$t(key),
            })
                .then((headers) => {
                    const list = Array.isArray(headers) ? headers : [];
                    const dataHeaders = list.filter((h) => h && h.value !== 'actions');
                    // Tabela ainda sem config no banco: mantém headers locais (não sobrescreve o pai com []).
                    if (dataHeaders.length === 0) {
                        this.tabelaFrontHeaders = [];
                        this.initLocalHeaders();
                        this.$emit('update:headers', this.localHeaders.map((h) => ({ ...h })));
                        return this.localHeaders;
                    }
                    this.tabelaFrontHeaders = list;
                    this.$emit('update:headers', list);
                    return list;
                })
                .catch((e) => {
                    console.log("Response : " + e.message);
                    if (e.message === "Request failed with status code 401") {
                        EventBus.$emit("show-error", { error: e });
                    }
                    this.initLocalHeaders();
                    return Promise.reject(e);
                })
                .finally(() => {
                    this.tabelaFrontHeadersLoading = false;
                });
        },
        onColumnVisibilityChange() {
            const updated = this.localHeaders.map((h) => ({ ...h }));
            if (this.effectiveSessionKey) {
                persistTabelaFrontHeadersSession(updated, this.effectiveSessionKey);
            }
            this.$emit('update:headers', updated);
        },
        ensureMultiSearchKeys() {
            let changed = false;
            this.filtrableHeaders.forEach((h) => {
                if (this.isDateRangeHeader(h)) {
                    const current = this.multiSearch[h.value];
                    if (!current || typeof current !== 'object' || Array.isArray(current)) {
                        this.$set(this.multiSearch, h.value, { from: null, to: null });
                        changed = true;
                    }
                } else if (!Object.prototype.hasOwnProperty.call(this.multiSearch, h.value)) {
                    this.$set(this.multiSearch, h.value, '');
                    changed = true;
                }
            });
            // Estrutura de chaves: aplica na hora (sem debounce) para filteredItems
            // já enxergar o mesmo mapa que a UI.
            if (changed) {
                this.scheduleApplyMultiSearch(true);
            }
        },
        /** Snapshot profundo o suficiente para dateRange { from, to } e strings. */
        cloneMultiSearch(val) {
            const out = {};
            if (!val || typeof val !== 'object') return out;
            Object.keys(val).forEach((k) => {
                const v = val[k];
                if (v != null && typeof v === 'object' && !Array.isArray(v)) {
                    out[k] = {
                        from: v.from != null ? v.from : null,
                        to: v.to != null ? v.to : null,
                    };
                } else {
                    out[k] = v;
                }
            });
            return out;
        },
        /**
         * Agenda applyMultiSearch. Digitação: debounce 400ms. Date-range / init: immediate.
         */
        scheduleApplyMultiSearch(immediate = false) {
            if (this._filterEmitTimeout) {
                clearTimeout(this._filterEmitTimeout);
                this._filterEmitTimeout = null;
            }
            if (immediate) {
                this.applyMultiSearch();
                return;
            }
            this._filterEmitTimeout = setTimeout(() => {
                this.applyMultiSearch();
                this._filterEmitTimeout = null;
            }, 400);
        },
        /**
         * Aplica multiSearch ao filtro client-side e, no modo server, emite update:filters.
         * Lê sempre this.multiSearch (estado atual), nunca um snapshot stale do watcher.
         */
        applyMultiSearch() {
            this.appliedMultiSearch = this.cloneMultiSearch(this.multiSearch);
            if (this.serverItemsLength == null) return;
            const source = this.multiSearch;
            const filters = Object.keys(source).reduce((acc, k) => {
                const v = source[k];
                if (v != null && typeof v === 'object' && !Array.isArray(v)) {
                    const from = v.from != null && String(v.from).trim() !== '' ? String(v.from).trim() : null;
                    const to = v.to != null && String(v.to).trim() !== '' ? String(v.to).trim() : null;
                    if (from || to) {
                        acc[k] = { from, to };
                    }
                } else if (v != null && String(v).trim() !== '') {
                    acc[k] = v;
                }
                return acc;
            }, {});
            this.$emit('update:filters', filters);
        },
        onFilterTextInput(column, value) {
            this.$set(this.multiSearch, column, value);
            // Digitação: debounce. O deep-watch também dispara; schedule reinicia o timer.
            this.scheduleApplyMultiSearch(false);
        },
        isDateRangeHeader(header) {
            if (!header) return false;
            if (header.filterType === 'dateRange') return true;
            return /^dt_.*_extenso$/.test(header.value || '');
        },
        headerFilterLabel(header) {
            const text = header.text;
            const val = this.multiSearch[header.value];
            if (this.isDateRangeHeader(header) && val && typeof val === 'object') {
                const from = val.from ? this.formatDateDisplay(val.from) : null;
                const to = val.to ? this.formatDateDisplay(val.to) : null;
                if (from && to) return `${text} (${from} — ${to})`;
                if (from) return `${text} (${this.$t('datetime.from')} ${from})`;
                if (to) return `${text} (${this.$t('datetime.until')} ${to})`;
                return text;
            }
            if (val != null && String(val).trim() !== '') {
                return `${text} (${val})`;
            }
            return text;
        },
        formatDateDisplay(isoDate) {
            if (!isoDate) return '';
            const s = String(isoDate).trim();
            const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (m) return `${m[3]}/${m[2]}/${m[1]}`;
            return s;
        },
        getDateRangeBound(column, bound) {
            const val = this.multiSearch[column];
            if (!val || typeof val !== 'object') return null;
            return val[bound] || null;
        },
        setDateRangeBound(column, bound, value) {
            const current = this.multiSearch[column];
            const next = (current && typeof current === 'object' && !Array.isArray(current))
                ? { ...current }
                : { from: null, to: null };
            next[bound] = value;
            this.$set(this.multiSearch, column, next);
            // Clique no calendário: aplica na hora (sem esperar debounce de digitação).
            this.scheduleApplyMultiSearch(true);
        },
        onDateRangePick(column, bound, value) {
            this.setDateRangeBound(column, bound, value || null);
            this.setDateMenuOpen(column, bound, false);
        },
        clearDateRange(column) {
            this.$set(this.multiSearch, column, { from: null, to: null });
            this.scheduleApplyMultiSearch(true);
        },
        dateMenuKey(column, bound) {
            return `${column}__${bound}`;
        },
        isDateMenuOpen(column, bound) {
            return !!this.dateRangeMenus[this.dateMenuKey(column, bound)];
        },
        setDateMenuOpen(column, bound, open) {
            this.$set(this.dateRangeMenus, this.dateMenuKey(column, bound), !!open);
        },
        parseCellDate(raw) {
            if (raw == null || raw === '') return null;
            const s = String(raw).trim();
            if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
                const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
                return Number.isNaN(d.getTime()) ? null : d;
            }
            const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?/);
            if (m) {
                const d = new Date(
                    Number(m[3]),
                    Number(m[2]) - 1,
                    Number(m[1]),
                    Number(m[4] || 0),
                    Number(m[5] || 0),
                    Number(m[6] || 0)
                );
                return Number.isNaN(d.getTime()) ? null : d;
            }
            return null;
        },
        matchesDateRange(cellValue, range) {
            const cell = this.parseCellDate(cellValue);
            if (!cell) return false;
            if (range.from) {
                const from = new Date(`${range.from}T00:00:00`);
                if (cell < from) return false;
            }
            if (range.to) {
                const to = new Date(`${range.to}T23:59:59`);
                if (cell > to) return false;
            }
            return true;
        },
        onUpdateOptions(opts) {
            if (opts) {
                this.tableOptions = {
                    page: opts.page || 1,
                    itemsPerPage: opts.itemsPerPage != null ? opts.itemsPerPage : this.itemsPerPage,
                };
            }
            if (this.serverItemsLength != null) {
                this.$emit('update:options', opts);
            } else if (opts && this.sortOptions.length >= 2) {
                const byArr = Array.isArray(opts.sortBy) ? opts.sortBy : (opts.sortBy != null ? [opts.sortBy] : []);
                const descArr = Array.isArray(opts.sortDesc) ? opts.sortDesc : (opts.sortDesc != null ? [opts.sortDesc === true || opts.sortDesc === 'true'] : []);
                if (byArr.length > 0) {
                    this.multiSort = byArr.map((col, i) => ({
                        value: col,
                        options: descArr[i] ? this.sortOptions[1] : this.sortOptions[0]
                    }));
                }
            }
        },
        virtualRangeForItems(pageItems) {
            return this.virtualRangeFor((pageItems || []).length);
        },
        virtualVisibleItems(pageItems) {
            const list = pageItems || [];
            if (!this.useVirtualBody) {
                return list;
            }
            const range = this.virtualRangeForItems(list);
            return list.slice(range.start, range.end);
        },
        virtualTopSpacerPx(pageItems) {
            return this.virtualRangeForItems(pageItems).start * (this.virtualRowHeight || 32);
        },
        virtualBottomSpacerPx(pageItems) {
            const list = pageItems || [];
            const range = this.virtualRangeForItems(list);
            return Math.max(0, (list.length - range.end) * (this.virtualRowHeight || 32));
        },
        virtualRangeFor(total) {
            const count = total || 0;
            if (!this.useVirtualBody || count <= 0) {
                return { start: 0, end: count };
            }
            const rowH = this.virtualRowHeight || 32;
            const wrapperH = this.virtualViewportHeightPx
                || Number(this.virtualViewportHeight)
                || 480;
            const headerH = this.virtualHeaderHeightPx || 0;
            const viewport = Math.max(rowH, wrapperH - headerH);
            const overscan = 8;
            const bodyScrollTop = Math.max(0, this.virtualScrollTop - headerH);
            const visibleCount = Math.ceil(viewport / rowH) + overscan * 2;
            let start = Math.max(0, Math.floor(bodyScrollTop / rowH) - overscan);
            let end = start + visibleCount;
            // No fim da lista o spacer inferior some e as linhas reais raramente
            // têm exatamente virtualRowHeight. Sem ancorar, start continua
            // acompanhando o scrollTop, o scrollHeight oscila e as linhas tremem.
            if (end >= count) {
                end = count;
                start = Math.max(0, count - visibleCount);
            }
            return { start, end };
        },
        virtualRowSlotProps(item, header, bodySlot, index) {
            return {
                item,
                header,
                value: item ? item[header.value] : undefined,
                headers: bodySlot.headers,
                index,
                isSelected: bodySlot.isSelected ? bodySlot.isSelected(item) : false,
                select: bodySlot.select,
                isExpanded: bodySlot.isExpanded ? bodySlot.isExpanded(item) : false,
                expand: bodySlot.expand,
            };
        },
        syncVirtualViewportMetrics() {
            const el = this._virtualScrollEl;
            if (!el) {
                return;
            }
            const viewportH = el.clientHeight;
            if (viewportH && viewportH !== this.virtualViewportHeightPx) {
                this.virtualViewportHeightPx = viewportH;
            }
            const thead = el.querySelector('thead');
            const headerH = thead ? thead.offsetHeight : 0;
            if (headerH !== this.virtualHeaderHeightPx) {
                this.virtualHeaderHeightPx = headerH;
            }
        },
        attachVirtualScroll() {
            const table = this.$refs.dataTable;
            const wrapper = table && table.$el && table.$el.querySelector('.v-data-table__wrapper');
            if (!wrapper) {
                return;
            }
            if (this._virtualScrollEl === wrapper) {
                this.syncVirtualViewportMetrics();
                return;
            }
            this.detachVirtualScroll();
            this._virtualScrollEl = wrapper;
            this._onVirtualScroll = () => {
                if (this._virtualScrollRaf) {
                    return;
                }
                this._virtualScrollRaf = requestAnimationFrame(() => {
                    this._virtualScrollRaf = null;
                    if (!this._virtualScrollEl) {
                        return;
                    }
                    const next = this._virtualScrollEl.scrollTop;
                    if (next !== this.virtualScrollTop) {
                        this.virtualScrollTop = next;
                    }
                });
            };
            wrapper.addEventListener('scroll', this._onVirtualScroll, { passive: true });
            this.syncVirtualViewportMetrics();
            if (!this.virtualViewportHeightPx) {
                this.virtualViewportHeightPx = Number(this.virtualViewportHeight) || 480;
            }
            if (typeof ResizeObserver !== 'undefined') {
                this._virtualResizeObserver = new ResizeObserver(() => {
                    this.syncVirtualViewportMetrics();
                });
                this._virtualResizeObserver.observe(wrapper);
            }
        },
        detachVirtualScroll() {
            if (this._virtualScrollRaf) {
                cancelAnimationFrame(this._virtualScrollRaf);
                this._virtualScrollRaf = null;
            }
            if (this._virtualScrollEl && this._onVirtualScroll) {
                this._virtualScrollEl.removeEventListener('scroll', this._onVirtualScroll);
            }
            if (this._virtualResizeObserver) {
                this._virtualResizeObserver.disconnect();
                this._virtualResizeObserver = null;
            }
            this._virtualScrollEl = null;
            this._onVirtualScroll = null;
        },
        resetVirtualScrollPosition() {
            this.virtualScrollTop = 0;
            if (this._virtualScrollEl) {
                this._virtualScrollEl.scrollTop = 0;
            }
        },
        eraseSort(key) {
            this.multiSort.splice(key, 1);
        },
        addSort() {
            if (this.sortableHeaders.length > 0) {
                this.multiSort.push({ value: this.sortableHeaders[0].value, options: { text: this.$t('system.labels.increasing'), value: 1 }});
            }
        },
        getItemClass(item) {
            if (typeof this.itemClass === 'function') {
                return this.itemClass(item);
            } else if (typeof this.itemClass === 'string') {
                return this.itemClass;
            }
            return '';
        },
        resolveItemClass(item) {
            const classes = [];
            const custom = this.getItemClass(item);
            if (custom) {
                classes.push(custom);
            }
            if (this.showItemSelect && this.isItemSelectActive(item)) {
                classes.push('merus-dt-item-selected');
            }
            if (this.showSelect && this.isRowChecked(item)) {
                classes.push('merus-dt-item-selected');
            }
            return classes.filter(Boolean).join(' ');
        },
        isItemSelectActive(item) {
            if (!item || !this.internalSelectedItem) return false;
            const itemKey = this.getItemKey(item);
            const selectedKey = this.getItemKey(this.internalSelectedItem);
            if (itemKey == null || selectedKey == null) return false;
            return String(itemKey) === String(selectedKey);
        },
        onItemSelectClick(item) {
            if (!item) return;
            const next = this.isItemSelectActive(item) ? null : item;
            this.internalSelectedItem = next;
            this.$emit('update:selectedItem', next);
            this.$emit('item-select', next);
            if (typeof this.itemSelectAction === 'function') {
                this.itemSelectAction(next);
            }
        },
        getItemKey(item) {
            if (!item) return null;
            return item[this.itemKey] || item.id || item.id_pessoa || null;
        },
        findItemIndex(item) {
            if (!item) return -1;
            const itemKey = this.getItemKey(item);
            const selection = this.effectiveSelection;
            return selection.findIndex(selected => {
                // Se o item selecionado é um objeto, comparar pela chave
                if (typeof selected === 'object' && selected !== null) {
                    const selectedKey = this.getItemKey(selected);
                    return selectedKey !== null && selectedKey === itemKey;
                }
                // Se é um valor primitivo, comparar diretamente
                return selected === itemKey;
            });
        },
        isItemSelected(item) {
            return this.findItemIndex(item) !== -1;
        },
        /** Marcado no multi-select (usa o estado interno, que reflete o checkbox na hora). */
        isRowChecked(item) {
            if (!item || !Array.isArray(this.selectedItems) || !this.selectedItems.length) {
                return false;
            }
            const itemKey = this.getItemKey(item);
            if (itemKey == null) return false;
            return this.selectedItems.some((selected) => {
                if (selected && typeof selected === 'object') {
                    const selectedKey = this.getItemKey(selected);
                    return selectedKey != null && String(selectedKey) === String(itemKey);
                }
                return String(selected) === String(itemKey);
            });
        },
        toggleItemSelection(item, value) {
            const index = this.findItemIndex(item);
            const selection = this.effectiveSelection;

            if (value) {
                if (index === -1) {
                    const newSelection = [...selection, item];
                    this.onSelectionInput(newSelection);
                }
            } else {
                if (index !== -1) {
                // Criar novo array para garantir reatividade
                    this.selectedItems = this.selectedItems.filter((_, i) => i !== index);
                }
            }
        },
        onSelectionInput(newSelection) {
            this.selectedItems = Array.isArray(newSelection) ? [...newSelection] : [];
            this.$emit('input', this.selectedItems);
        },
        onItemSelected(payload) {
            this.$emit('item-selected', payload);
        },
        onToggleSelectAll(payload) {
            this.$emit('toggle-select-all', payload);
        },
        resetNewColumnForm() {
            this.newColumnLabelTouched = false;
            this.newColumn = {
                value: null,
                text: '',
                label: '',
                to_show: true,
                to_export: true,
            };
            if (this.$refs.addColumnForm) {
                this.$refs.addColumnForm.resetValidation();
            }
        },
        openAddColumnDialog() {
            this.resetNewColumnForm();
            this.addColumnDialog = true;
        },
        closeAddColumnDialog() {
            this.addColumnDialog = false;
            this.resetNewColumnForm();
        },
        onNewColumnFieldSelected(value) {
            if (!value) return;
            if (!this.newColumn.text) {
                this.newColumn.text = this.formatFieldLabel(value);
            }
            if (!this.newColumnLabelTouched || !this.newColumn.label) {
                this.newColumn.label = this.newColumn.text;
            }
        },
        onNewColumnTextInput(value) {
            if (!this.newColumnLabelTouched) {
                this.newColumn.label = value;
            }
        },
        formatFieldLabel(value) {
            return String(value)
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (c) => c.toUpperCase());
        },
        saveNewColumn() {
            if (!this.$refs.addColumnForm || !this.$refs.addColumnForm.validate()) {
                return;
            }
            if (!this.codTabela) return;

            const token = this.token || sessionStorage.getItem('api');
            this.addColumnSaving = true;

            apiController.request(apiController.endpoints.insertHeader, {
                data: {
                    cod_tabela: this.codTabela,
                    value: this.newColumn.value,
                    text: this.newColumn.text.trim(),
                    label: (this.newColumn.label || this.newColumn.text).trim(),
                    to_show: this.newColumn.to_show ? 1 : 0,
                    to_export: this.newColumn.to_export ? 1 : 0,
                },
                config: { headers: { 'x-access-token': token } },
                silent: true,
                onSuccess: (response) => {
                    if (response.data && response.data.erro) {
                        EventBus.$emit('show-alert', {
                            mensagem: response.data.mensagem || this.$t('system.messages.saveError'),
                            tipo: 'error',
                        });
                        this.addColumnSaving = false;
                        return;
                    }
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('system.labels.columnAddedSuccess'),
                        tipo: 'success',
                    });
                    this.closeAddColumnDialog();
                    this.loadTabelaFrontHeaders(true).then((headers) => {
                        this.$emit('column-added', {
                            codTabela: this.codTabela,
                            header: response.data.header,
                            headers,
                        });
                    });
                    this.addColumnSaving = false;
                },
                onError: (e) => {
                    const msg =
                        (e.response && e.response.data && e.response.data.mensagem) ||
                        this.$t('system.messages.saveError');
                    EventBus.$emit('show-alert', { mensagem: msg, tipo: 'error' });
                    this.addColumnSaving = false;
                },
            });
        },
    }
    
}
</script>
<style>
.merus-dt-item-selected {
  background-color: rgba(25, 118, 210, 0.12) !important;
}
.merus-dt-virtual-spacer {
  border: none !important;
}
.merus-dt-virtual-spacer > td {
  padding: 0 !important;
  border: none !important;
  line-height: 0 !important;
  vertical-align: top;
}
.merus-dt-virtual-spacer__fill {
  width: 1px;
  pointer-events: none;
}
</style>
